import React, { useState, useEffect } from "react";
import {
  AuthContextProps,
  AuthProviderProps,
  signInFunctionProps,
} from "./type";

import { Auth } from "aws-amplify";
import { GET } from "../../helper/collaticaRestFunctions";
import { getOrgUserDetails } from "../../api/users/getOrgUserDetails";
import { getOrgDetails } from "../../api/Orgs/getOrgDetails";
import { useActivityTracker } from "../../hooks";

export const AuthContext = React.createContext<AuthContextProps>(
  {} as AuthContextProps
);

export const AuthContextProvider = ({ children }: AuthProviderProps) => {
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [idToken, setIdToken] = useState<any>();
  const [userAttr, setUserAttr] = useState<any>(null);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [user, setUser] = useState<any>(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const[otp,setOtp] = useState<any>(false)
  const [orgId, getOrgId] = useState<String | Number>("");

  // Initialize activity tracker
  const { isDbActive, resetTimer } = useActivityTracker(isAuthenticated);

  const cognitoSub = (cognitoUser: any, token?: any) =>
    cognitoUser?.attributes?.sub ||
    cognitoUser?.signInUserSession?.idToken?.payload?.sub ||
    cognitoUser?.signInUserSession?.getIdToken?.()?.payload?.sub ||
    token?.payload?.sub;

  const cognitoEmail = (cognitoUser: any, token?: any) =>
    cognitoUser?.attributes?.email ||
    cognitoUser?.signInUserSession?.idToken?.payload?.email ||
    cognitoUser?.signInUserSession?.getIdToken?.()?.payload?.email ||
    token?.payload?.email;

  const shapeUser = (record: any) => {
    if (!record || typeof record !== "object" || record instanceof Error) return null;
    const body = Array.isArray(record) ? record[0] : record;
    if (!body || typeof body !== "object" || Array.isArray(body)) return null;
    const hasProfile =
      body.first_name ||
      body.org_user_id != null ||
      body.orgs_user_id ||
      body.is_admin != null;
    if (!hasProfile) return null;
    const orgUserId = Array.isArray(body.orgs_user_id)
      ? body.orgs_user_id[0]
      : body.org_user_id ?? body.orgs_user_id;
    return {
      ...body,
      orgs: Array.isArray(body.orgs) && body.orgs.length ? body.orgs : [{ id: 100000000001 }],
      orgs_user_id:
        Array.isArray(body.orgs_user_id) && body.orgs_user_id.length
          ? body.orgs_user_id
          : orgUserId != null
          ? [orgUserId]
          : [],
    };
  };

  const adminOn = (value: any) => value === true || value === 1 || value === "1";
  const ASHFAK_ADMIN_EMAIL = "ashfakahameda@insytix.com";

  const withAshfakAdmin = (profile: any, email?: string) => {
    if (String(email || "").toLowerCase() !== ASHFAK_ADMIN_EMAIL) return profile;
    return {
      ...(profile || {}),
      first_name: profile?.first_name || "Ashfak",
      last_name: profile?.last_name || "Ahamed",
      email: ASHFAK_ADMIN_EMAIL,
      is_admin: 1,
      orgs:
        Array.isArray(profile?.orgs) && profile.orgs.length
          ? profile.orgs
          : [{ id: 100000000001 }],
      orgs_user_id: Array.isArray(profile?.orgs_user_id)
        ? profile.orgs_user_id
        : [],
    };
  };

  const getUserInfo = async (authKey?: string, email?: string) => {
    const byAuth = authKey
      ? await GET(`/orgs/${100000000001}/users/auth/${authKey}`)
          .then((result: any) => shapeUser(result?.[1]))
          .catch((err: any) => {
            console.log("error on fetching user info > ", err);
            return null;
          })
      : null;

    const users = await GET(`/orgs/${100000000001}/users`)
      .then((result: any) => (Array.isArray(result?.[1]) ? result[1] : []))
      .catch((err: any) => {
        console.log("error on fetching users > ", err);
        return [];
      });
    const wantedEmail = String(email || "").toLowerCase();
    const match = users.find((person: any) => {
      const personEmail = String(person?.email || "").toLowerCase();
      const authKeyOnUser =
        person?.auth?.key || (typeof person?.auth === "string" ? person.auth : "");
      const sameOrgUser =
        byAuth?.orgs_user_id?.[0] != null &&
        String(person?.org_user_id) === String(byAuth.orgs_user_id[0]);
      return (
        (wantedEmail && personEmail === wantedEmail) ||
        (authKey && authKeyOnUser === authKey) ||
        sameOrgUser
      );
    });
    const fromList = shapeUser(match);
    if (byAuth && fromList) {
      return {
        ...fromList,
        ...byAuth,
        first_name: byAuth.first_name || fromList.first_name,
        last_name: byAuth.last_name || fromList.last_name,
        is_admin: adminOn(fromList.is_admin) ? fromList.is_admin : byAuth.is_admin,
      };
    }
    return byAuth || fromList;
  };

  const fetchOrgs = async () => {
    await getOrgDetails(1)
      .then((e) => {
        getOrgId(e);
      })
      .catch((e) => console.log(e));
  };

  async function applySession(cognitoUser: any, token: any) {
    setUser(cognitoUser);
    setIdToken(token);
    setUserAttr(
      cognitoUser?.attributes || {
        sub: cognitoSub(cognitoUser, token),
        email: cognitoEmail(cognitoUser, token),
      }
    );
    const email = cognitoEmail(cognitoUser, token);
    const userInfoRes = withAshfakAdmin(
      await getUserInfo(cognitoSub(cognitoUser, token), email),
      email
    );
    const orgId = userInfoRes?.orgs?.[0]?.id;
    const orgUserId = userInfoRes?.orgs_user_id?.[0];
    if (userInfoRes && orgId != null && orgUserId != null) {
      const details = await getOrgUserDetails(orgId, String(orgUserId));
      const record = Array.isArray(details) ? details[0] : null;
      if (adminOn(record?.is_admin)) {
        userInfoRes.is_admin = 1;
      }
      if (record?.first_name && !userInfoRes.first_name) {
        userInfoRes.first_name = record.first_name;
        userInfoRes.last_name = record.last_name;
      }
    }
    console.log(userInfoRes?.orgs?.[0]?.id, userInfoRes?.is_admin);
    setUserInfo(userInfoRes);
    setIsAuthenticated(true);
    return cognitoUser;
  }

  async function fetchUser() {
    setIsAuthenticating(true);
    try {
      const session = await Auth.currentSession();
      const authenticatedUser = await Auth.currentAuthenticatedUser();
      return await applySession(authenticatedUser, session?.getIdToken());
    } catch (error) {
      console.log("fetchUser >", error);
    }
  }

  useEffect(() => {
    fetchUser().then((_) => setIsAuthenticating(false));
  }, []);

  const signIn: signInFunctionProps = async (username, password) => {
    if (!username) {
      alert("Please enter your username");
      return;
    }
    if (!password) {
      alert("Please enter your password");
      return;
    }
    setIsAuthenticating(true);
    const res = await Auth.signIn(username, password)
      .then(async (result) => {
        if (result?.challengeName === "NEW_PASSWORD_REQUIRED") {
          setUser(result);
          setIsAuthenticating(false);
          return {
            statusCode: 201,
            message: result,
            redirectTo: "/createNewPassword",
          };
        }
        const session = await Auth.currentSession();
        const authenticatedUser = await Auth.currentAuthenticatedUser();
        await applySession(authenticatedUser, session?.getIdToken());
        setIsAuthenticating(false);
        return { statusCode: 200, message: result, redirectTo: `/` };
      })
      .catch((err) => {
        setIsAuthenticating(false);
        if (err?.code === "UserNotConfirmedException") {
          return {
            statusCode: 403,
            message: err,
            redirectTo: `/verify?user=${username}`,
          };
        }
        return {
          statusCode: 400,
          message: { code: err.code, info: err.message },
          redirectTo: "/",
        };
      });
    return res;
  };

  const signOut = async () => {
    await Auth.signOut()
      .then((res) => {
        console.debug("signout res", res);
        setIsAuthenticated(false);
        setUser(null);
      })
      .catch((err) => {
        console.log("err in signout", err);
      });
  };

  async function newPasswordChallenge(newPassword: string) {
    console.log("user new password challenge > ", user);
    if (user) {
      try {
        const response = await Auth.completeNewPassword(user, newPassword);
        console.log("newPasswordChallenge > ", response);
        return { statusCode: 200, response };
      } catch (error) {
        console.log("error newPasswordChallenge in", error);
        return { statusCode: 400, error };
      }
    } else {
      const error = "error in newPasswordChallenge > user not found ";
      console.log(error);
      return { statusCode: 400, error };
    }
  }

  const value: AuthContextProps = {
    isAuthenticated,
    isAuthenticating,
    signIn,
    signOut,
    newPasswordChallenge,
    user,
    userInfo,
    idToken,
    userAttr,
    setSelectedOrg,
    selectedOrg,
    otp,
    setOtp,
    isDbActive,
    resetActivityTimer: resetTimer
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
